<template>
    <!-- Clickable card for in-progress steps with URL or completed enhanced screening steps -->
    <button 
        v-if="url && (status === 'Ready to Start' || (status === 'Completed' && isCompletedEnhancedStep))" 
        @click="navigateToUrl"
        type="button"
        class="block transition-transform hover:scale-105 h-full w-full text-left"
    >
        <div :class="[
            'flex flex-col divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow cursor-pointer hover:shadow-lg w-full h-full',
            isCompletedEnhancedStep ? 'border-l-4 border-green-500' : 'border-l-4 border-blue-500',
            isNextStep && !isCompletedEnhancedStep ? 'ring-2 ring-blue-500 bg-blue-50' : ''
        ]">
            <div class="px-4 py-5 sm:px-6 flex justify-between">
                <div class="flex items-center">
                    <div class="flex items-center">
                        <span v-if="stepNumber" class="flex items-center justify-center w-8 h-8 text-white text-sm font-bold rounded-full mr-3"
                              :class="isCompletedEnhancedStep ? 'bg-green-600' : 'bg-blue-600'">
                            {{ stepNumber }}
                        </span>
                        <font-awesome-icon v-if="iconDefinition" :icon="iconDefinition" size="2x" class="mr-2" /> 
                        <span class="font-semibold">{{ header }}</span>
                    </div>
                </div>
                <div class="flex items-center">
                    <span v-if="isNextStep && !isCompletedEnhancedStep" class="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full mr-2">
                        NEXT
                    </span>
                    <button v-if="isNextStep && !isCompletedEnhancedStep" class="hidden sm:inline-block bg-blue-600 text-white px-3 py-1 rounded-md text-xs font-medium hover:bg-blue-700 transition-colors mr-2">
                        Start This Step
                    </button>
                    <span v-if="isCompletedEnhancedStep" class="text-xs text-green-600 mr-2">Done</span>
                    <font-awesome-icon 
                        v-if="!isCompletedEnhancedStep" 
                        :icon="faLockOpen" 
                        size="2x" 
                        class="mr-2 text-gray-500" 
                    />
                    <font-awesome-icon 
                        v-if="isCompletedEnhancedStep" 
                        :icon="faCircleCheck" 
                        size="2x" 
                        class="text-green-700" 
                    />
                </div>
            </div>
            <div class="flex-grow px-4 py-5 sm:p-6" v-html="body"></div>
            <!-- <div class="px-4 py-4 sm:px-6 border-t border-gray-200 flex justify-between items-center">
                <span>{{ footer }}</span>
            </div> -->
        </div>
    </button>

    <!-- Non-clickable card for completed steps or steps without URL -->
    <div v-else :class="[
        isLocked ? 'cursor-not-allowed border-l-4 border-gray-400 bg-gray-50' : (status === 'Completed' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'),
        'flex flex-col divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow h-full'
    ]">
        <div class="px-4 py-5 sm:px-6 flex justify-between">
            <div class="flex items-center">
                <div class="flex items-center">
                    <span v-if="stepNumber" class="flex items-center justify-center w-8 h-8 rounded-full mr-3 text-sm font-bold" 
                          :class="isLocked ? 'bg-gray-400 text-white' : (status === 'Completed' ? 'bg-green-600 text-white' : 'bg-gray-400 text-white')">
                        {{ stepNumber }}
                    </span>
                    <font-awesome-icon v-if="iconDefinition" :icon="iconDefinition" size="2x" class="mr-2" /> 
                    <span class="font-semibold" :class="isLocked ? 'text-gray-500' : 'text-gray-900'">{{ header }}</span>
                </div>
            </div>
            <div class="flex items-center">
                <span v-if="isLocked" class="text-xs text-gray-500 mr-2">{{ locked_hint }}</span>
                <span v-if="status === 'Completed'" class="text-xs text-green-600 mr-2">Done</span>
                <font-awesome-icon v-if="!isCompletedEnhancedStep" :icon="faLock" size="2x" class="text-gray-500 mr-2" />
                <font-awesome-icon v-if="status === 'Completed'" :icon="faCircleCheck" size="2x" class="text-green-700" />
            </div>
        </div>
        <div class="flex-grow px-4 py-5 sm:p-6" v-html="body" :class="isLocked ? 'text-gray-500' : ''"></div>
        <!-- <div class="px-4 py-4 sm:px-6 border-t border-gray-200" :class="isLocked ? 'text-gray-500' : ''">
            {{ isLocked ? 'This step will be available after completing the previous steps.' : footer }}
        </div> -->
    </div>
</template>

<script>
    import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
    import { router } from '@inertiajs/vue3';
    import axios from 'axios';
    import { 
        faUser, faHome, faFileContract, faCreditCard, faCheck,
        faBuilding, faMapPin, faCalendarAlt, faIdCard, faDollarSign, faShareFromSquare, faFiles, faFileMagnifyingGlass,
        faLock, faLockOpen, faLandmark
    } from '@fortawesome/pro-light-svg-icons';

    import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

    // Create an icon map for commonly used icons
    const iconMap = {
        'user': faUser,
        'home': faHome,
        'file-contract': faFileContract,
        'credit-card': faCreditCard,
        'check': faCheck,
        'files': faFiles,
        'building': faBuilding,
        'map-pin': faMapPin,
        'calendar': faCalendarAlt,
        'id-card': faIdCard,
        'dollar': faDollarSign,
        'share-from-square': faShareFromSquare,
        'file-magnifying-glass': faFileMagnifyingGlass,
        'landmark': faLandmark
    };
    
    export default {
        components: {
            FontAwesomeIcon
        },
        props: {
            header: {
                type: String,
                default: ''
            },
            step_name: {
                type: String,
                default: ''
            },
            icon: {
                type: [String, Array, Object],
                default: ''
            },
            body: {
                type: String,
                default: ''
            },
            footer: {
                type: String,
                default: ''
            },
            status: {
                type: String,
                default: ''
            },
            url: {
                type: String,
                default: null
            },
            isLocked: {
                type: Boolean,
                default: false
            },
            application: {
                type: Object,
                default: () => ({})
            },
            addon_id: {
                type: [String, Number],
                default: null
            },
            step_number: {
                type: [String, Number],
                default: null
            },
            is_next_step: {
                type: Boolean,
                default: false
            },
            /**
             * What a locked card tells the user to do first. Defaults to the
             * application flow's original wording so existing callers are
             * unaffected; flows with different prerequisites pass their own.
             */
            locked_hint: {
                type: String,
                default: 'Complete Step 1 First'
            },
            /**
             * Keeps a completed step clickable so the user can open it again.
             * The hardcoded enhanced-screening list already does this for some
             * steps; this lets a caller opt in without editing that list.
             */
            revisitable: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                faCircleCheck,
                faLock,
                faLockOpen,
                isLoader: false
            };
        },
        computed: {
            iconDefinition() {
                if (!this.icon) return null;
                
                // If it's already an icon object, return it
                if (typeof this.icon === 'object') {
                    return this.icon;
                }
                
                // If it's a string, look it up in the icon map
                if (typeof this.icon === 'string') {
                    // Clean up the icon name
                    let iconName = this.icon.replace(/^fa-/, '').replace(/^fas?\s+fa-/, '');
                    
                    // Return from icon map or fallback to string format
                    return iconMap[iconName] || ['fas', iconName];
                }
                
                return this.icon;
            },
            isCompletedEnhancedStep() {
                // Check if this is a completed step that should remain clickable
                const enhancedSteps = ['income_verification', 'assets', 'payroll', 'personal_information'];
                return this.status === 'Completed' && (this.revisitable || enhancedSteps.includes(this.step_name));
            },
            stepNumber() {
                return this.step_number;
            },
            isNextStep() {
                return this.is_next_step;
            }
        },
        methods: {
            navigateToUrl() {
                if (!this.url) return;
                
                // Check if this is a special createLinkToken URL
                if (this.url === 'createLinkToken' && this.step_name) {
                    this.handleEnhancedScreening();
                } else {
                    // Normal navigation
                    router.visit(this.url, {
                        preserveState: true,
                        preserveScroll: true
                    });
                }
            },
            
            handleEnhancedScreening() {
                // Create an action object based on step_name
                const action = {
                    addon: {
                        unique_name: this.step_name,
                        id: this.addon_id
                    }
                };
                
                // Call the createLinkToken method with the action
                this.createLinkToken(action);
            },
            
            async createLinkToken(action) {
                if (action.addon.unique_name === 'income_verification') {
                    router.visit(route('user-incomes.application', this.application.id));
                    return false;
                }
                else if (action.addon.unique_name === 'assets') {
                    router.visit(route('user-assets.application', [this.application.id]));
                    return false;
                }
                else if (action.addon.unique_name === 'payroll') {
                    router.visit(route('user-employment-verification.application', [this.application.id]));
                    return false;
                }
                if (action.complete) {
                    return false;
                }
                this.isLoader = true;
                try {
                    // Ensure CSRF cookie exists immediately before posting
                    await axios.get('/sanctum/csrf-cookie');
                    const response = await axios.post(route('plaid.create-link-token'), {
                        product: action.addon.unique_name,
                    });
                    if (response.data.status) {
                        this.initializePlaidLink(response.data.public_token, action.addon);
                    }

                    // Handle the successful response here
                } catch (error) {
                    // Handle any errors here
                    console.error('Error:', error.response ? error.response.data : error.message);
                }
            },

            async initializePlaidLink(public_token, product) {
                this.isLoader = false;
                var handler = Plaid.create({
                    token: public_token,  // Use the provided public token directly
                    onLoad: function () {
                        // Optional, called when Link loads
                    },
                    onSuccess: (public_token, metadata) => {
                        this.isLoader = true;
                        if (product.unique_name === 'auth') {
                            this.finishAuth(public_token, metadata, product);
                        } else if (product.unique_name === 'identity_verification') {
                            this.finishIdv(public_token, metadata, product);
                        }
                    },
                    onExit: function (err, metadata) {
                        // The user exited the Link flow.
                        if (err != null) {
                            // The user encountered a Plaid API error prior to exiting.
                        }
                        // metadata contains information about the institution
                        // that the user selected and the most recent API request IDs.
                        // Storing this information can be helpful for support.
                    },
                    onEvent: function (eventName, metadata) {
                        // Optionally capture Link flow events, streamed through
                        // this callback as your users connect an Item to Plaid.
                        // For example:
                        // eventName = "TRANSITION_VIEW"
                        // metadata  = {
                        //   link_session_id: "123-abc",
                        //   mfa_type:        "questions",
                        //   timestamp:       "2017-09-14T14:42:19.350Z",
                        //   view_name:       "MFA",
                        // }
                    }
                });

                handler.open();
            },

            async finishAuth(public_token, metadata, product) {
                const response = await axios.post(route('plaid.exchange-token-and-retrieve-account'), {
                    public_token: public_token,
                    metadata: metadata,
                });
                this.isLoader = false;
                router.reload();
            },
            async finishIdv(public_token, metadata, product) {
                const response = await axios.post(route('plaid.idv.submit.application'), {
                    public_token: public_token,
                    metadata: metadata,
                    application_id: this.application.id,
                    addon_id: product.id, // This now contains the correct addon_id
                });
                this.isLoader = false;

                location.reload();
            }
        }
    }
</script>